import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionIT = () => {


  const faqs = [
    {
      q: "What is IT consulting and what does an IT consultant do?",
      a: "IT consulting helps organizations align technology investments with business strategy, operating goals, and measurable outcomes. Consultants evaluate current architecture, process maturity, security posture, and delivery bottlenecks to identify practical improvement paths. The role is not only advisory; it includes roadmap definition, prioritization sequencing, and implementation governance support. At Libsys, consulting engagements are designed to convert strategy into execution plans that teams can actually deliver. This ensures technology change improves performance, resilience, and long-term scalability rather than creating isolated initiatives."
    },
    {
      q: "What are the benefits of hiring an IT consulting firm?",
      a: "An IT consulting partner brings cross-industry expertise, structured frameworks, and delivery acceleration that are difficult to build internally at the same speed. Organizations benefit from faster decision-making because architecture options, risk implications, and implementation paths are evaluated with practical context. Consulting also reduces costly trial-and-error by validating priorities against business value and technical feasibility before major investment. Security and governance are embedded earlier in the lifecycle, lowering long-term operational risk. This allows leadership teams to focus on growth and customer outcomes while technology transformation progresses with clear accountability."
    },
    {
      q: "How do I choose the right IT consulting company for my business?",
      a: "Choose a consulting partner that demonstrates both technical depth and delivery discipline in environments similar to yours. Evaluate their ability to translate business objectives into executable roadmaps with measurable milestones and governance controls. The right firm should provide transparent communication, realistic risk planning, and clear ownership across strategy and execution phases. Industry relevance, security maturity, and integration experience are critical factors when selecting a long-term transformation partner. A strong consulting relationship should improve decision quality, implementation confidence, and organizational capability over time."
    },
    {
      q: "What types of services do IT consulting firms provide?",
      a: "Typical services include technology strategy, enterprise architecture, cloud modernization, cybersecurity enhancement, and operating model optimization. Many engagements also include application rationalization, integration planning, data platform design, and delivery governance setup. At implementation stage, consulting teams often support vendor selection, migration sequencing, and quality controls for major transformation programs. Post-implementation support may include performance tuning, capability enablement, and roadmap recalibration based on outcomes. The exact service mix is shaped by your business goals, constraints, and current technology maturity."
    },
    {
      q: "What is IT strategy consulting and why is it important?",
      a: "IT strategy consulting creates a structured plan that links business priorities to technology capabilities, funding, and execution sequencing. It is important because many organizations invest in tools without a cohesive operating model, resulting in fragmented value realization. A strategy-led approach defines target architecture, modernization priorities, and governance checkpoints before major implementation spend. This improves alignment across business, product, security, and operations stakeholders from the beginning. The outcome is a realistic roadmap that supports growth goals while managing risk, cost, and organizational change."
    },
    {
      q: "How can IT consulting services benefit small businesses?",
      a: "Small and mid-sized businesses benefit by gaining access to specialized expertise without hiring a large permanent internal consulting function. Consulting helps prioritize high-impact initiatives so limited budgets are allocated to changes that improve operational and customer outcomes fastest. It also enables stronger security and governance foundations early, which prevents expensive remediation later. Cloud and automation adoption can be phased in practical increments, reducing disruption while improving scalability. This approach helps growing businesses build enterprise-grade capability with controlled investment and execution clarity."
    },
    {
      q: "What are the top IT consulting firms and what sets them apart?",
      a: "Top firms like Libsys set themselves apart through unmatched experience, fully customized strategies, and a proactive approach to continuous optimization and support."
    },
    {
      q: "How do IT consulting firms support digital transformation initiatives?",
      a: "Consulting firms support digital transformation by combining strategic planning with structured execution oversight. They identify priority domains where technology modernization will create measurable value and define phased delivery plans with clear milestone governance. During implementation, they manage architecture decisions, integration dependencies, and risk mitigation to keep programs stable and outcome-focused. They also support organizational enablement so teams can adopt new tools and workflows effectively. This end-to-end approach improves transformation speed while protecting business continuity and quality."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="IT Consulting"
        description="Libsys IT consulting — architecture modernization, cloud and security strategy, execution governance. Align technology investments with long-term business objectives."
        path="/solutions/it"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                IT Consulting & Roadmap
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Expert Guidance for <span className="text-secondary">Technology Strategy</span>
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                True IT consulting is more than just troubleshooting; it is about aligning your technical architecture with your long-term business strategy. Libsys provides the strategic oversight and engineering depth needed to navigate complex digital transformations. We help organizations modernize legacy systems, migrate to secure cloud environments, and establish robust cybersecurity frameworks that protect their most critical assets. By combining lifecycle governance with hands-on technical execution, we ensure that your technology investments become a primary driver of sustainable growth and operational resilience.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'Business-aligned IT roadmaps',
                  'Cloud & platform modernization',
                  'Security & governance integration',
                  'Program execution oversight',
                  'Enterprise architecture design',
                  'Continuous tech optimization'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Consult an Expert
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                 <img
                    src="/Itconsulting.png"
                    alt="IT consulting simulation"
                    className="w-full h-[400px] lg:h-[600px] object-cover object-left group-hover:scale-105 transition-transform duration-1000"
                    onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                  />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ TRANSFORMATION PILLARS SECTION ══════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Execution-Focused Consulting</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              We bridge the gap between high-level strategy and technical reality, delivering tangible roadmaps that account for your unique operational constraints.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Architecture Alignment", desc: "We design target architectures that move away from legacy debt and toward scalable, API-first ecosystems that support future innovation." },
              { title: "Risk Mitigation", desc: "Every consulting phase includes proactive security assessments and governance reviews to protect your data and compliance posture." },
              { title: "Cloud Optimization", desc: "Strategic sizing, multi-cloud strategy, and cost governance plans that ensure your cloud transition remains sustainable and high-performing." }
            ].map((pillar, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{pillar.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Architecture & Strategy</span>
              <h2 className="text-h2 text-primary mb-6">Outcome-Driven Consulting Framework</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Libsys consulting services are built on the principle that technology should always follow business purpose. We don't just recommend tools; we help you build an operating model that supports agility and scale. By conducting deep-dive assessments of your existing delivery gaps, we help you prioritize high-impact modernization projects that deliver measurable ROI while reducing your overall technical complexity. Our consultants stay engaged from strategic scoping through technical implementation to ensure your vision becomes reality.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Enterprise Stack", desc: "Optimizing the full technology stack for maximum delivery speed." },
                  { title: "SecOps Governance", desc: "Integrating security into the core of your delivery lifecycle." },
                  { title: "Cloud Enablement", desc: "From multi-cloud strategy to cost-efficient containerization." },
                  { title: "Stability Audit", desc: "Proactive performance tuning to prevent operational downtime." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Technology roadmaps aligned with 3-5 year growth plans',
                  'Vendor-neutral assessments focused on your specific ROI',
                  'Rigorous governance checkpoints at every project phase'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
               <div className="rounded-[2.5rem] overflow-hidden border border-[#236086]/10 shadow-2xl bg-white p-2">
                 <img
                  src="/itconsulkting.png"
                  alt="IT consulting operations"
                  className="w-full h-auto aspect-square lg:aspect-[4/3] object-cover rounded-[2.2rem]"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ MAPPING ══════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center mb-16">
          <span className="text-strategic-tagline block mb-4 italic">Capabilities Overview</span>
          <h2 className="text-h2 text-primary mb-4">Enterprise Consulting Portfolio</h2>
          <p className="text-slate-500 max-w-2xl mx-auto font-medium leading-relaxed italic">Future-proof your IT infrastructure with our expert strategic consulting solutions.</p>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-10 bg-slate-50/50 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-primary mb-4">IT Strategy Consulting</h3>
                <p className="text-slate-600 text-small leading-relaxed font-medium">Developing long-term roadmaps that align technology investments with your business vision and strategic growth goals. We ensure your tool acquisition matches your operating model maturity.</p>
            </div>
            <div className="p-10 bg-slate-50/50 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-xl font-bold text-primary mb-4">Strategic Transformation</h3>
                <p className="text-slate-600 text-small leading-relaxed font-medium">Providing high-touch guidance to revamp your architecture and adopt innovative technologies like Cloud and AI. We manage the integration dependencies so your program remains stable.</p>
            </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">Transformation Ready</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Uplift Your IT Infrastructure with <span className="text-secondary">Libsys</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                Build a practical modernization plan with architecture clarity, cloud scalability, and security controls aligned to business outcomes. accelerate execution with structured milestones.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Coverage', v: 'Strategy to Execution' },
                  { k: 'Model', v: 'Advisory + Delivery' },
                  { k: 'Focus', v: 'Risk-Managed Modernization' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionIT;
